// Full-viewport fixed storybook forest backdrop — Snow-White-inspired enchanted woodland.
// Supports two seasonal variants: "summer" (default, bright day) and "winter" (snowy).
// Includes a hidden cottage on the middle hill with a warm-lit window and smoke curling from the chimney.
// `contained=true` renders it as an absolutely-positioned layer inside its parent (useful for share cards).
export default function ForestBackdrop({ variant = "summer", contained = false }) {
  const isWinter = variant === "winter";

  const palette = isWinter
    ? {
        sky:      ["#DCEEFB", "#EEF6FD", "#FCFCFC", "#F6EAD4"], // cool pale sky → warm sunset horizon
        hillFar:  ["#E9F0F5", "#C6D3DE"],
        hillMid:  ["#D6E1EA", "#A6BCCC"],
        hillNear: ["#EFF5F9", "#BFCDD9"],
        tree:     ["#3B7A46", "#144320"],
        treeDark: ["#2F6539", "#0F3319"],
        pathColor: "#DCE9F1",
        pathHi:    "#F4F9FC",
        flowerColors: ["#FF6B9D", "#FFFFFF", "#FFFFFF", "#FFD93D"],
        cottage: {
          roof: "#F4F9FC",
          wall: "#E8D8B7",
          door: "#7A4A24",
          window: "#FFE58A",
        },
        showSnow: true,
      }
    : {
        sky:      ["#BDE0F5", "#F5E6C0", "#F1D08E", "#DDBB6E"],
        hillFar:  ["#9BD48F", "#5FA65E"],
        hillMid:  ["#5FA65E", "#2F7A3D"],
        hillNear: ["#3F8A44", "#22582B"],
        tree:     ["#4E9C57", "#1E5A2A"],
        treeDark: ["#3A7A45", "#144320"],
        pathColor: "#E7C48A",
        pathHi:    "#F0D8A6",
        flowerColors: ["#FF6B9D", "#FFD93D", "#B49FFF", "#FF6B9D", "#FFD93D", "#FFFFFF"],
        cottage: {
          roof: "#7A4A24",
          wall: "#E8D8B7",
          door: "#5C3A22",
          window: "#FFE58A",
        },
        showSnow: false,
      };

  return (
    <div
      aria-hidden="true"
      data-testid={`forest-backdrop-${variant}`}
      style={{
        position: contained ? "absolute" : "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <svg
        viewBox="0 0 1920 1080"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <defs>
          <linearGradient id={`fbSky-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0"    stopColor={palette.sky[0]} />
            <stop offset="0.45" stopColor={palette.sky[1]} />
            <stop offset="0.78" stopColor={palette.sky[2]} />
            <stop offset="1"    stopColor={palette.sky[3]} />
          </linearGradient>
          <linearGradient id={`fbHillFar-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={palette.hillFar[0]} />
            <stop offset="1" stopColor={palette.hillFar[1]} />
          </linearGradient>
          <linearGradient id={`fbHillMid-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={palette.hillMid[0]} />
            <stop offset="1" stopColor={palette.hillMid[1]} />
          </linearGradient>
          <linearGradient id={`fbHillNear-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={palette.hillNear[0]} />
            <stop offset="1" stopColor={palette.hillNear[1]} />
          </linearGradient>
          <linearGradient id={`fbTree-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={palette.tree[0]} />
            <stop offset="1" stopColor={palette.tree[1]} />
          </linearGradient>
          <linearGradient id={`fbTreeDark-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={palette.treeDark[0]} />
            <stop offset="1" stopColor={palette.treeDark[1]} />
          </linearGradient>
          <radialGradient id={`fbSun-${variant}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0"   stopColor="#FFF6BF" stopOpacity="0.95" />
            <stop offset="0.6" stopColor="#FFE58A" stopOpacity="0.55" />
            <stop offset="1"   stopColor="#FFE58A" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={`fbGlow-${variant}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#FFF6BF" stopOpacity="0.55" />
            <stop offset="1" stopColor="#FFF6BF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`fbBeam-${variant}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#FFF6BF" stopOpacity="0.35" />
            <stop offset="1" stopColor="#FFF6BF" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={`fbWindow-${variant}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#FFFCE1" />
            <stop offset="0.7" stopColor={palette.cottage.window} />
            <stop offset="1" stopColor="#F0B347" />
          </radialGradient>
        </defs>

        {/* Sky */}
        <rect x="0" y="0" width="1920" height="1080" fill={`url(#fbSky-${variant})`} />

        {/* Warm sun in the clearing */}
        <circle cx="960" cy="440" r="360" fill={`url(#fbSun-${variant})`} />
        <circle cx="960" cy="440" r="120" fill={`url(#fbGlow-${variant})`} />

        {/* Soft dappled light circles */}
        <circle cx="700"  cy="380" r="80"  fill="#FFF6BF" opacity="0.20" />
        <circle cx="1220" cy="360" r="90"  fill="#FFF6BF" opacity="0.20" />
        <circle cx="530"  cy="480" r="60"  fill="#FFF6BF" opacity="0.16" />
        <circle cx="1400" cy="500" r="65"  fill="#FFF6BF" opacity="0.16" />
        <circle cx="960"  cy="620" r="120" fill="#FFF6BF" opacity={isWinter ? 0.22 : 0.14} />

        {/* Diagonal sunbeams */}
        <g opacity={isWinter ? 0.28 : 0.35}>
          <polygon points="880,120 1040,120 1180,900 720,900"  fill={`url(#fbBeam-${variant})`} />
          <polygon points="1080,150 1160,150 1300,900 1000,900" fill={`url(#fbBeam-${variant})`} />
          <polygon points="740,150 820,150  980,900  620,900"   fill={`url(#fbBeam-${variant})`} />
        </g>

        {/* Distant far hills */}
        <path
          d="M -20 720 Q 320 620 620 700 T 1220 690 T 1940 700 L 1940 1080 L -20 1080 Z"
          fill={`url(#fbHillFar-${variant})`}
          opacity="0.85"
        />

        {/* Mid hills — the cottage sits on this one */}
        <path
          d="M -20 820 Q 420 720 780 800 T 1400 810 T 1940 820 L 1940 1080 L -20 1080 Z"
          fill={`url(#fbHillMid-${variant})`}
          opacity="0.95"
        />

        {/* HIDDEN COTTAGE on the mid hill (offset right) */}
        <g transform="translate(1220 720)">
          {/* Base shadow */}
          <ellipse cx="45" cy="130" rx="120" ry="12" fill="#000" opacity="0.15" />

          {/* Body / walls */}
          <rect x="0" y="35" width="140" height="90" fill={palette.cottage.wall} />
          {/* Cross-hatched cedar-shake / plaster texture on walls (subtle) */}
          <g stroke="#7A4A24" strokeWidth="0.8" opacity="0.28">
            <line x1="6"  y1="52" x2="66" y2="52" />
            <line x1="6"  y1="60" x2="66" y2="60" />
            <line x1="6"  y1="68" x2="66" y2="68" />
            <line x1="6"  y1="98" x2="66" y2="98" />
            <line x1="6"  y1="106" x2="66" y2="106" />
            <line x1="6"  y1="114" x2="66" y2="114" />
            <line x1="80" y1="52" x2="128" y2="52" />
            <line x1="80" y1="60" x2="128" y2="60" />
            <line x1="80" y1="68" x2="128" y2="68" />
          </g>
          {/* Wooden beams (Tudor style) */}
          <rect x="0"   y="35"  width="140" height="6"  fill="#7A4A24" />
          <rect x="0"   y="119" width="140" height="6"  fill="#7A4A24" />
          <rect x="0"   y="35"  width="6"   height="90" fill="#7A4A24" />
          <rect x="134" y="35"  width="6"   height="90" fill="#7A4A24" />
          <line x1="70" y1="35" x2="70" y2="125" stroke="#7A4A24" strokeWidth="4" />
          <line x1="10" y1="80" x2="60" y2="80" stroke="#7A4A24" strokeWidth="3" />
          <line x1="80" y1="80" x2="130" y2="80" stroke="#7A4A24" strokeWidth="3" />

          {/* Warmly lit window */}
          <rect x="14"  y="50" width="42" height="26" rx="3" fill={`url(#fbWindow-${variant})`} />
          <line x1="35" y1="50" x2="35" y2="76" stroke="#7A4A24" strokeWidth="2" />
          <line x1="14" y1="63" x2="56" y2="63" stroke="#7A4A24" strokeWidth="2" />
          <rect x="12"  y="46" width="46" height="6" rx="1.5" fill="#7A4A24" />
          {/* Wooden shutters flanking the window */}
          <rect x="4"   y="48" width="7"  height="30" rx="1" fill="#5C3A22" />
          <rect x="59"  y="48" width="7"  height="30" rx="1" fill="#5C3A22" />
          <line x1="7.5" y1="52" x2="7.5" y2="74" stroke="#3A2515" strokeWidth="0.8" />
          <line x1="62.5" y1="52" x2="62.5" y2="74" stroke="#3A2515" strokeWidth="0.8" />
          {/* Flower box under the window */}
          <rect x="10" y="76" width="50" height="8" rx="1.5" fill="#5C3A22" />
          <rect x="10" y="76" width="50" height="2" fill="#7A4A24" />
          {!isWinter && (
            <g>
              {/* Little blossoms poking out of the box */}
              <circle cx="16" cy="74" r="2.4" fill="#FF6B9D" />
              <circle cx="16" cy="72" r="1.4" fill="#FFD93D" />
              <circle cx="26" cy="73" r="2.6" fill="#FFFFFF" />
              <circle cx="26" cy="71" r="1.4" fill="#FFD93D" />
              <circle cx="36" cy="74" r="2.4" fill="#B49FFF" />
              <circle cx="36" cy="72" r="1.4" fill="#FFD93D" />
              <circle cx="46" cy="73" r="2.4" fill="#FF6B9D" />
              <circle cx="46" cy="71" r="1.4" fill="#FFD93D" />
              <circle cx="55" cy="74" r="2.2" fill="#6EC4E8" />
              {/* trailing leaves */}
              <path d="M 12 84 Q 10 88 12 92" stroke="#3F8A44" strokeWidth="1.4" fill="none" strokeLinecap="round" />
              <path d="M 58 84 Q 60 88 58 92" stroke="#3F8A44" strokeWidth="1.4" fill="none" strokeLinecap="round" />
            </g>
          )}
          {isWinter && (
            <>
              {/* snow tuft on the flower box */}
              <path d="M 10 76 Q 20 71 30 74 Q 40 70 50 74 Q 55 72 60 76 Z" fill="#FFFFFF" />
            </>
          )}

          {/* Door */}
          <rect x="84" y="86" width="30" height="39" rx="2" fill={palette.cottage.door} />
          <path d="M 84 86 Q 99 76 114 86" fill={palette.cottage.door} />
          {/* Door plank lines */}
          <line x1="94"  y1="90" x2="94"  y2="123" stroke="#3A2515" strokeWidth="0.9" />
          <line x1="104" y1="90" x2="104" y2="123" stroke="#3A2515" strokeWidth="0.9" />
          {/* Door frame trim */}
          <rect x="82"  y="84" width="34" height="4" rx="1" fill="#5C3A22" />
          <circle cx="108" cy="106" r="1.8" fill="#FFD93D" />

          {/* Cobblestone path leading to the door */}
          <g opacity="0.9">
            <ellipse cx="99"  cy="130" rx="9" ry="3" fill="#B7A98E" />
            <ellipse cx="99"  cy="130" rx="7" ry="2" fill="#D9CFB4" />
            <ellipse cx="103" cy="138" rx="9" ry="3" fill="#B7A98E" />
            <ellipse cx="103" cy="138" rx="7" ry="2" fill="#D9CFB4" />
            <ellipse cx="97"  cy="146" rx="10" ry="3" fill="#B7A98E" />
            <ellipse cx="97"  cy="146" rx="8"  ry="2" fill="#D9CFB4" />
          </g>

          {/* Thatched / snowy roof */}
          <path
            d="M -10 40 Q 70 -12 150 40 L 138 46 L 2 46 Z"
            fill={palette.cottage.roof}
          />
          {/* Thatch texture lines */}
          <g stroke="#5C3A22" strokeWidth="0.9" opacity="0.35" fill="none">
            <path d="M 4 42 Q 20 24 40 22" />
            <path d="M 24 44 Q 40 20 60 15" />
            <path d="M 46 45 Q 62 15 82 15" />
            <path d="M 68 45 Q 82 15 100 20" />
            <path d="M 90 45 Q 104 20 122 26" />
            <path d="M 110 44 Q 122 26 138 38" />
          </g>
          {isWinter && (
            <>
              <path
                d="M -6 42 Q 70 -8 146 42 Q 138 40 130 42 Q 100 30 70 30 Q 40 30 10 42 Q 2 40 -6 42 Z"
                fill="#FFFFFF"
              />
              {/* icicles */}
              <path d="M 12 46 L 10 56 L 14 46 Z" fill="#FFFFFF" />
              <path d="M 44 46 L 42 58 L 46 46 Z" fill="#FFFFFF" />
              <path d="M 82 46 L 80 55 L 84 46 Z" fill="#FFFFFF" />
              <path d="M 118 46 L 116 58 L 120 46 Z" fill="#FFFFFF" />
            </>
          )}

          {/* Chimney */}
          <rect x="98" y="6" width="18" height="34" fill="#8A5A3A" />
          <rect x="96" y="4" width="22" height="6"  fill="#5C3A22" />
          {/* chimney brick texture */}
          <g stroke="#5C3A22" strokeWidth="0.6" opacity="0.55">
            <line x1="98" y1="14" x2="116" y2="14" />
            <line x1="98" y1="22" x2="116" y2="22" />
            <line x1="98" y1="30" x2="116" y2="30" />
            <line x1="106" y1="10" x2="106" y2="14" />
            <line x1="102" y1="18" x2="102" y2="22" />
            <line x1="110" y1="18" x2="110" y2="22" />
            <line x1="106" y1="26" x2="106" y2="30" />
          </g>
          {isWinter && <rect x="96" y="0" width="22" height="6" fill="#FFFFFF" />}
          {/* Smoke */}
          <circle cx="107" cy="-6"  r="7"  fill="#FFFFFF" opacity="0.85" />
          <circle cx="112" cy="-20" r="9"  fill="#FFFFFF" opacity="0.72" />
          <circle cx="104" cy="-36" r="11" fill="#FFFFFF" opacity="0.60" />
          <circle cx="116" cy="-54" r="13" fill="#FFFFFF" opacity="0.45" />

          {/* Little tree next to cottage */}
          <g transform="translate(-30 60)">
            <rect x="-3" y="30" width="6" height="12" fill="#3A2515" />
            <path d="M -18 30 L 0 -10 L 18 30 Z" fill={isWinter ? "#3B7A46" : "#3F8A44"} />
            <path d="M -14 12 L 0 -20 L 14 12 Z"  fill={isWinter ? "#4E9C57" : "#4E9C57"} />
            {isWinter && (
              <>
                <path d="M -18 30 L 0 -10 L 18 30 L 14 30 L 0 -4 L -14 30 Z" fill="#FFFFFF" opacity="0.85" />
                <path d="M -14 12 L 0 -20 L 14 12 L 10 12 L 0 -14 L -10 12 Z" fill="#FFFFFF" opacity="0.9" />
              </>
            )}
          </g>

          {/* Storybook garden: little shrubs + wildflowers along the base */}
          {!isWinter && (
            <g>
              {/* left rosebush */}
              <ellipse cx="-8" cy="126" rx="14" ry="9" fill="#3F8A44" />
              <ellipse cx="-14" cy="122" rx="7" ry="5" fill="#4E9C57" />
              <ellipse cx="-2"  cy="121" rx="6" ry="4" fill="#4E9C57" />
              <circle cx="-14" cy="121" r="1.8" fill="#FF6B9D" />
              <circle cx="-6"  cy="119" r="1.8" fill="#FFD93D" />
              <circle cx="0"   cy="122" r="1.8" fill="#FF6B9D" />

              {/* right shrub near door */}
              <ellipse cx="132" cy="128" rx="16" ry="10" fill="#3F8A44" />
              <ellipse cx="125" cy="123" rx="8" ry="6" fill="#4E9C57" />
              <ellipse cx="140" cy="124" rx="7" ry="5" fill="#4E9C57" />
              <circle cx="126" cy="120" r="1.8" fill="#B49FFF" />
              <circle cx="134" cy="118" r="1.8" fill="#FFD93D" />
              <circle cx="142" cy="121" r="1.8" fill="#FF6B9D" />

              {/* scattered wildflowers on the walk */}
              <circle cx="76"  cy="132" r="1.8" fill="#FF6B9D" />
              <circle cx="80"  cy="131" r="0.9" fill="#FFD93D" />
              <circle cx="120" cy="140" r="1.8" fill="#B49FFF" />
              <circle cx="124" cy="139" r="0.9" fill="#FFD93D" />
              <circle cx="70"  cy="146" r="1.6" fill="#6EC4E8" />
            </g>
          )}
          {isWinter && (
            <g>
              {/* snow mounds along the base */}
              <ellipse cx="-8"  cy="126" rx="16" ry="7" fill="#FFFFFF" />
              <ellipse cx="132" cy="128" rx="18" ry="8" fill="#FFFFFF" />
              <ellipse cx="99"  cy="150" rx="20" ry="4" fill="#FFFFFF" opacity="0.9" />
            </g>
          )}
        </g>

        {/* Front hills */}
        <path
          d="M -20 940 Q 500 860 900 920 T 1600 940 T 1940 950 L 1940 1080 L -20 1080 Z"
          fill={`url(#fbHillNear-${variant})`}
        />

        {/* LEFT FLANK — cluster of tall pines */}
        <g>
          <FBTree x={90}  base={1060} h={780} w={200} colorId={`fbTreeDark-${variant}`} snow={isWinter} />
          <FBTree x={260} base={1060} h={700} w={190} colorId={`fbTree-${variant}`}    snow={isWinter} />
          <FBTree x={-40} base={1060} h={640} w={230} colorId={`fbTreeDark-${variant}`} snow={isWinter} />
        </g>

        {/* RIGHT FLANK */}
        <g>
          <FBTree x={1830} base={1060} h={780} w={200} colorId={`fbTreeDark-${variant}`} snow={isWinter} flip />
          <FBTree x={1660} base={1060} h={700} w={190} colorId={`fbTree-${variant}`}    snow={isWinter} flip />
          <FBTree x={1960} base={1060} h={640} w={230} colorId={`fbTreeDark-${variant}`} snow={isWinter} flip />
        </g>

        {/* Winding path in the center foreground */}
        <path
          d="M 960 1080 Q 900 980 940 900 Q 990 830 960 760 Q 930 700 970 640"
          stroke={palette.pathColor}
          strokeWidth="70"
          strokeLinecap="round"
          fill="none"
          opacity="0.65"
        />
        <path
          d="M 960 1080 Q 900 980 940 900 Q 990 830 960 760 Q 930 700 970 640"
          stroke={palette.pathHi}
          strokeWidth="46"
          strokeLinecap="round"
          fill="none"
          opacity="0.9"
        />

        {/* Toadstools in front hills (both seasons) */}
        <g transform="translate(360 990)">
          <path d="M -10 0 Q -8 24 0 28 Q 8 24 10 0 Z" fill="#FFFFFF" />
          <path d="M -26 0 Q -24 -26 0 -26 Q 24 -26 26 0 Z" fill={isWinter ? "#B84C6D" : "#E63C6B"} />
          <circle cx="-10" cy="-12" r="4" fill="#FFFFFF" />
          <circle cx="8"   cy="-16" r="5" fill="#FFFFFF" />
          <circle cx="14"  cy="-4"  r="4" fill="#FFFFFF" />
          {isWinter && <path d="M -22 -22 Q -12 -30 0 -30 Q 12 -30 22 -22 Q 12 -26 0 -26 Q -12 -26 -22 -22 Z" fill="#FFFFFF" />}
        </g>
        <g transform="translate(1560 995)">
          <path d="M -8 0 Q -6 20 0 22 Q 6 20 8 0 Z" fill="#FFFFFF" />
          <path d="M -22 0 Q -20 -22 0 -22 Q 20 -22 22 0 Z" fill={isWinter ? "#C08135" : "#FFB347"} />
          <circle cx="-8" cy="-10" r="3.5" fill="#FFFFFF" />
          <circle cx="6"  cy="-14" r="4"   fill="#FFFFFF" />
        </g>

        {/* Peeking bear cubs — nod to "The World Needs Your Light" */}
        <FBPeekingBear x={150} y={950} scale={1} winter={isWinter} />
        <FBPeekingBear x={1770} y={945} scale={1.05} winter={isWinter} flip />
        <FBPeekingBear x={860} y={1000} scale={0.75} winter={isWinter} />

        {/* Flowers on the hills (summer) — or snow-mounds (winter) */}
        {palette.flowerColors.map((c, i) => {
          const positions = [[520, 970], [640, 985], [780, 975], [1120, 970], [1260, 990], [1400, 975]];
          const [x, y] = positions[i];
          return (
            <g key={i} transform={`translate(${x} ${y})`}>
              {isWinter ? (
                <ellipse cx="0" cy="4" rx="14" ry="4" fill="#FFFFFF" opacity="0.9" />
              ) : (
                <>
                  <circle r="5" fill={c} />
                  <circle r="2" fill="#FFD93D" />
                </>
              )}
            </g>
          );
        })}

        {/* Fireflies (summer) or falling snowflakes (winter) */}
        {isWinter ? (
          <g>
            {[
              [80, 60], [180, 220], [320, 90], [440, 300], [560, 140], [680, 240],
              [820, 90], [960, 200], [1080, 60], [1200, 260], [1320, 100], [1440, 280],
              [1560, 160], [1700, 60], [1840, 200], [240, 400], [780, 400], [1300, 420],
              [420, 540], [1160, 560], [640, 660], [1500, 620], [900, 720], [300, 780],
            ].map(([x, y], i) => (
              <g key={i} transform={`translate(${x} ${y})`}>
                <circle r={2 + (i % 3)} fill="#FFFFFF" opacity="0.95" />
                <circle r={(2 + (i % 3)) * 2.2} fill="#FFFFFF" opacity="0.18" />
              </g>
            ))}
          </g>
        ) : (
          <g>
            {[
              [420, 460, 5], [520, 380, 4], [1440, 500, 5], [1360, 400, 4],
              [720, 300, 5], [1200, 320, 4], [880, 280, 5], [1080, 250, 4],
            ].map(([x, y, s], i) => (
              <g key={i} transform={`translate(${x} ${y})`}>
                <circle r={s} fill="#FFF6BF" opacity="0.9" />
                <circle r={s * 2.4} fill="#FFF6BF" opacity="0.22" />
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

function FBPeekingBear({ x, y, scale = 1, winter = false, flip = false }) {
  // Golden cub matching "The World Needs Your Light" — head + ears peeking.
  const fur = winter ? "#B98550" : "#BF864A";
  const furDark = winter ? "#7A4E23" : "#7E4E23";
  const ear = winter ? "#F4C3C0" : "#F0B4B0";
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Fluffy tree/shrub the bear peeks out from behind */}
      <ellipse cx="0" cy="34" rx="46" ry="12" fill={winter ? "#F1F6FA" : "#3F8A44"} opacity="0.9" />
      <ellipse cx={-30 * dir} cy="30" rx="18" ry="10" fill={winter ? "#DDEBF3" : "#4E9C57"} opacity="0.9" />
      {/* Ears */}
      <circle cx="-16" cy="0" r="8" fill={fur} />
      <circle cx="-16" cy="1.5" r="3.5" fill={ear} />
      <circle cx="16" cy="0" r="8" fill={fur} />
      <circle cx="16" cy="1.5" r="3.5" fill={ear} />
      {/* Head */}
      <ellipse cx="0" cy="14" rx="22" ry="20" fill={fur} />
      {/* Muzzle */}
      <ellipse cx="0" cy="22" rx="10" ry="7" fill={winter ? "#F7E1B6" : "#F7E1B6"} />
      {/* Nose */}
      <ellipse cx="0" cy="18" rx="2.6" ry="2" fill="#3D2818" />
      {/* Sleepy happy eyes */}
      <path d="M -8 12 Q -6 10 -4 12" stroke="#3D2818" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      <path d="M 4 12 Q 6 10 8 12" stroke="#3D2818" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      {/* Rosy cheeks */}
      <circle cx="-12" cy="20" r="2" fill={ear} opacity="0.85" />
      <circle cx="12" cy="20" r="2" fill={ear} opacity="0.85" />
      {/* One tiny paw peeking below the bush */}
      <ellipse cx={-14 * dir} cy="36" rx="6" ry="4" fill={furDark} />
      <ellipse cx={-14 * dir} cy="37" rx="3" ry="2" fill={ear} opacity="0.7" />
    </g>
  );
}

function FBTree({ x, base, h, w, colorId, flip = false, snow = false }) {
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x} ${base - h})`}>
      <rect x={-w * 0.05} y={h * 0.85} width={w * 0.1} height={h * 0.15} fill="#3A2515" />
      <path d={`M ${-w / 2} ${h * 0.85} L 0 ${h * 0.45} L ${w / 2} ${h * 0.85} Z`} fill={`url(#${colorId})`} />
      <path d={`M ${-w / 2.3} ${h * 0.62} L 0 ${h * 0.22} L ${w / 2.3} ${h * 0.62} Z`} fill={`url(#${colorId})`} />
      <path d={`M ${-w / 2.7} ${h * 0.36} L 0 ${0} L ${w / 2.7} ${h * 0.36} Z`} fill={`url(#${colorId})`} />
      <path
        d={`M ${dir * -w * 0.14} ${h * 0.24} L 0 ${h * 0.08} L ${dir * w * 0.14} ${h * 0.24} Z`}
        fill="#7FDDB8" opacity="0.35"
      />
      {snow && (
        <g>
          {/* Snow on each tier */}
          <path d={`M ${-w / 2.7 + 4} ${h * 0.36 - 2} L 0 ${2} L ${w / 2.7 - 4} ${h * 0.36 - 2} Q ${w / 4} ${h * 0.34} 0 ${h * 0.30} Q ${-w / 4} ${h * 0.34} ${-w / 2.7 + 4} ${h * 0.36 - 2} Z`} fill="#FFFFFF" />
          <path d={`M ${-w / 2.3 + 5} ${h * 0.62 - 2} L 0 ${h * 0.24} L ${w / 2.3 - 5} ${h * 0.62 - 2} Q ${w / 3.4} ${h * 0.60} 0 ${h * 0.56} Q ${-w / 3.4} ${h * 0.60} ${-w / 2.3 + 5} ${h * 0.62 - 2} Z`} fill="#FFFFFF" opacity="0.95" />
          <path d={`M ${-w / 2 + 6} ${h * 0.85 - 2} L 0 ${h * 0.48} L ${w / 2 - 6} ${h * 0.85 - 2} Q ${w / 3} ${h * 0.82} 0 ${h * 0.78} Q ${-w / 3} ${h * 0.82} ${-w / 2 + 6} ${h * 0.85 - 2} Z`} fill="#FFFFFF" opacity="0.90" />
        </g>
      )}
    </g>
  );
}
